/**
 * Pure editing logic for the Manage Manual Assets modal (AST-116).
 *
 * The modal holds a list of draft rows. Each draft is either a saved row
 * (has `id`) or a new one (has `clientId`, no `id`). Everything here is
 * pure so the rules -- the bits most likely to be subtly wrong -- are
 * tested without rendering.
 */

export const NAME_MAX = 20;
export const DESCRIPTION_MAX = 250;
export const DESCRIPTION_COUNTER_FROM = 200;

let seq = 0;
export const newClientId = () => `new-${Date.now().toString(36)}-${(seq += 1)}`;

/** Server rows -> drafts. `original` is kept to work out what changed. */
export const toDrafts = (items) => items.map((r) => ({
  id: r.id,
  name: r.name,
  description: r.description || '',
  isArchived: !!r.isArchived,
  original: { name: r.name, description: r.description || '', isArchived: !!r.isArchived },
}));

export const blankDraft = () => ({ clientId: newClientId(), name: '', description: '', isArchived: false });

export const isNew = (d) => !d.id;
export const keyOf = (d) => d.id || d.clientId;

/** MOD-28: a new row the user never typed into. */
export const isBlankNew = (d) => isNew(d) && !d.name.trim() && !d.description.trim() && !d.isArchived;

export const isDirty = (d) => isNew(d)
  ? !isBlankNew(d)
  : d.name.trim() !== d.original.name
    || d.description.trim() !== d.original.description
    || d.isArchived !== d.original.isArchived;

/** MOD-30: Save is enabled only when something would actually change. */
export const hasChanges = (drafts) => drafts.some(isDirty);

/** MOD-5: closing needs confirmation if anything was typed, even a blank-ish row. */
export const hasUnsavedEdits = (drafts) => drafts.some((d) => (isNew(d) ? !isBlankNew(d) : isDirty(d)));

/** Natural, case-insensitive A->Z, so ST84 sorts before ST124 (MOD-18). */
export const naturalCompare = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/**
 * MOD-18: saved rows by name, new rows at the bottom in the order added.
 * Sorted by each saved row's ORIGINAL name, so a row doesn't jump while the
 * user is still typing in it.
 */
export const orderDrafts = (drafts) => [
  ...drafts.filter((d) => !isNew(d)).sort((a, b) => naturalCompare(a.original.name, b.original.name)),
  ...drafts.filter(isNew),
];

/** MOD-9 / MOD-10 counts, from the drafts as they'd save. */
export const tally = (drafts) => {
  const live = drafts.filter((d) => !isBlankNew(d));
  const archived = live.filter((d) => d.isArchived).length;
  return { active: live.length - archived, archived };
};

/**
 * MOD-23..27: field errors for every draft, keyed by draft key.
 * Uniqueness (MOD-24) is case-insensitive across every row in the modal,
 * archived included -- the modal holds the subtype's full set.
 * Blank new rows are skipped (MOD-28): they're dropped, not failed.
 *
 * A duplicate is reported on the row(s) the user CHANGED, never on an
 * untouched saved row it collides with: if you add "st124" the problem is
 * the new row, not the existing ST124. (Same rule the server applies.) If
 * every row involved is untouched -- which only happens with pre-existing
 * bad data -- nothing is flagged rather than blaming a row the user can't
 * have caused.
 */
export const validateDrafts = (drafts) => {
  const errors = {};
  const set = (k, field, message) => { (errors[k] = errors[k] || {})[field] = message; };

  const counted = drafts.filter((d) => !isBlankNew(d));
  const byName = new Map();
  for (const d of counted) {
    const n = d.name.trim().toLowerCase();
    if (!n) continue;
    if (!byName.has(n)) byName.set(n, []);
    byName.get(n).push(d);
  }
  const touched = (d) => isNew(d) || d.name.trim() !== d.original.name;

  for (const d of counted) {
    const k = keyOf(d);
    const name = d.name.trim();
    if (!name) set(k, 'name', 'Name is required.');
    else if (name.length > NAME_MAX) set(k, 'name', `Max ${NAME_MAX} characters.`);
    else if (byName.get(name.toLowerCase()).length > 1 && touched(d)) set(k, 'name', 'Name already used.');
    if (d.description.length > DESCRIPTION_MAX) set(k, 'description', `Max ${DESCRIPTION_MAX} characters.`);
  }
  return errors;
};

/** Merge the server's per-row errors into the same shape (ref = id or clientId). */
export const serverErrorsToMap = (fieldErrors = []) => {
  const out = {};
  for (const e of fieldErrors) {
    if (!e.ref) continue;
    (out[e.ref] = out[e.ref] || {})[e.field] = e.message;
  }
  return out;
};

/** The request body for PUT .../batch. Blank new rows dropped (MOD-28). */
export const buildBatch = (drafts, { subtypeId, assetTypeId, version }) => ({
  subtypeId,
  assetTypeId,
  version,
  creates: drafts.filter((d) => isNew(d) && !isBlankNew(d)).map((d) => ({
    clientId: d.clientId,
    name: d.name.trim(),
    description: d.description.trim(),
    isArchived: d.isArchived,
  })),
  updates: drafts.filter((d) => !isNew(d) && isDirty(d)).map((d) => ({
    id: d.id,
    name: d.name.trim(),
    description: d.description.trim(),
    isArchived: d.isArchived,
  })),
});

/** Saved rows being archived in this save -- each may need a usage check (MOD-37). */
export const newlyArchived = (drafts) => drafts.filter((d) => !isNew(d) && d.isArchived && !d.original.isArchived);
